// © Thibault Sabathier, 2026. frontend/src/hooks/useAttachmentComposer.js
//
// État du composeur de pièces jointes : sélection, compression, aperçus,
// retrait avant envoi, glisser-déposer et collage.
//
// La logique PURE (classification, compression, plafonds, extraction depuis un
// presse-papier ou un dépôt) vit dans `utils/messageAttachments.js`, qui est
// testé. Ce hook n'orchestre que ce qui exige un navigateur : le cycle de vie
// des ObjectURL d'aperçu et l'état de survol du dépôt.
//
// ── POURQUOI UN COMPTEUR DE PROFONDEUR POUR LE DRAG ───────────────────────
// `dragenter` et `dragleave` se déclenchent aussi en traversant les ENFANTS de
// la zone. Un simple booléen fait donc clignoter l'overlay dès que le curseur
// passe au-dessus d'un message. On compte les entrées et les sorties.
//
// ── POURQUOI RÉVOQUER LES ObjectURL ───────────────────────────────────────
// Chaque `createObjectURL` retient le Blob en mémoire jusqu'à révocation
// explicite. Sans ça, joindre puis retirer dix photos dans une session laisse
// dix images en mémoire pour toute la durée de vie de l'onglet.

import { useState, useCallback, useEffect, useRef } from 'react'
import {
  prepareAttachments,
  checkSelectionLimits,
  filesFromClipboard,
  filesFromDrop,
} from '../utils/messageAttachments'

export const useAttachmentComposer = ({ enabled }) => {
  const [entries, setEntries] = useState([])
  const [preparing, setPreparing] = useState(false)
  const [dragging, setDragging] = useState(false)

  const dragDepth = useRef(0)
  // Les URL d'aperçu vivantes, pour pouvoir toutes les révoquer au démontage
  // même si le composant disparaît sans passer par `remove`.
  const liveUrls = useRef(new Set())

  const createPreview = useCallback((entry) => {
    if (entry.status !== 'ready' || entry.kind !== 'image' || !entry.file) return entry
    if (typeof URL === 'undefined' || !URL.createObjectURL) return entry
    const url = URL.createObjectURL(entry.file)
    liveUrls.current.add(url)
    return { ...entry, previewUrl: url }
  }, [])

  const revoke = useCallback((url) => {
    if (!url) return
    if (liveUrls.current.has(url)) {
      URL.revokeObjectURL(url)
      liveUrls.current.delete(url)
    }
  }, [])

  // Révocation au démontage. Dépendances vides et lecture par ref : la liste
  // des URL ne doit PAS relancer cet effet, sinon chaque ajout révoquerait les
  // aperçus précédents.
  useEffect(
    () => () => {
      liveUrls.current.forEach((url) => URL.revokeObjectURL(url))
      liveUrls.current.clear()
    },
    [],
  )

  /**
   * Ajoute des fichiers à la sélection courante.
   *
   * Les entrées REFUSÉES sont conservées : c'est la seule façon d'afficher la
   * raison du refus à côté du fichier concerné. L'appelant les retire d'un
   * clic, ou elles disparaissent à l'envoi.
   */
  const addFiles = useCallback(
    async (files) => {
      // Garde de capacité côté client, en plus du masquage du bouton : un
      // dépôt ou un collage ne passe pas par le bouton.
      if (!enabled) return

      const list = Array.from(files || [])
      if (list.length === 0) return

      setPreparing(true)
      try {
        const prepared = await prepareAttachments(list)
        setEntries((current) => [...current, ...prepared.map(createPreview)])
      } finally {
        // `finally` et non après le `await` : une exception inattendue
        // laisserait sinon le composeur bloqué sur « préparation… ».
        setPreparing(false)
      }
    },
    [enabled, createPreview],
  )

  const remove = useCallback(
    (id) => {
      setEntries((current) => {
        const target = current.find((e) => e.id === id)
        if (target) revoke(target.previewUrl)
        return current.filter((e) => e.id !== id)
      })
    },
    [revoke],
  )

  const clear = useCallback(() => {
    setEntries((current) => {
      current.forEach((e) => revoke(e.previewUrl))
      return []
    })
    dragDepth.current = 0
    setDragging(false)
  }, [revoke])

  // ── Glisser-déposer ──────────────────────────────────────────────────────

  const onDragEnter = useCallback(
    (event) => {
      if (!enabled) return
      // Ne réagir qu'à un dépôt de FICHIERS : sans ce filtre, sélectionner du
      // texte dans un message et le faire glisser afficherait l'overlay.
      const types = Array.from(event.dataTransfer?.types || [])
      if (!types.includes('Files')) return
      event.preventDefault()
      dragDepth.current += 1
      setDragging(true)
    },
    [enabled],
  )

  const onDragOver = useCallback(
    (event) => {
      if (!enabled) return
      const types = Array.from(event.dataTransfer?.types || [])
      if (!types.includes('Files')) return
      // Sans preventDefault sur dragover, le navigateur REFUSE le dépôt et
      // ouvre le fichier dans un nouvel onglet.
      event.preventDefault()
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
    },
    [enabled],
  )

  const onDragLeave = useCallback(
    (event) => {
      if (!enabled) return
      // Volontairement PAS de filtre sur `types` ici, contrairement à
      // `dragenter` : plusieurs navigateurs rendent une liste VIDE sur
      // `dragleave`. Filtrer laisserait alors le compteur au-dessus de zéro et
      // l'overlay affiché en permanence après un survol. `Math.max(0, …)`
      // rend la décrémentation inoffensive quand rien n'avait été compté.
      event.preventDefault()
      dragDepth.current = Math.max(0, dragDepth.current - 1)
      if (dragDepth.current === 0) setDragging(false)
    },
    [enabled],
  )

  const onDrop = useCallback(
    (event) => {
      if (!enabled) return
      event.preventDefault()
      dragDepth.current = 0
      setDragging(false)
      const files = filesFromDrop(event.dataTransfer)
      if (files.length > 0) addFiles(files)
    },
    [enabled, addFiles],
  )

  /**
   * Collage. Ne consomme l'événement QUE s'il portait des fichiers, pour que
   * le collage de texte continue de fonctionner normalement dans la saisie.
   */
  const onPaste = useCallback(
    (event) => {
      if (!enabled) return
      const files = filesFromClipboard(event.clipboardData)
      if (files.length === 0) return
      event.preventDefault()
      addFiles(files)
    },
    [enabled, addFiles],
  )

  const limits = checkSelectionLimits(entries)
  const readyFiles = entries.filter((e) => e.status === 'ready').map((e) => e.file)

  return {
    entries,
    readyFiles,
    preparing,
    dragging,
    limits,
    // `limits.code` couvre les plafonds de lot ; les entrées refusées portent
    // leur propre code. Les deux doivent être levés pour pouvoir envoyer.
    blocked: Boolean(limits.code),
    addFiles,
    remove,
    clear,
    dragHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
    onPaste,
  }
}

export default useAttachmentComposer
